import { useEffect, useMemo, useCallback } from "react";
import {
  Spinner,
  Alert,
  Container,
  Form,
  Row,
  Col,
  Badge,
  Card,
} from "react-bootstrap";

export const AddOnTabContent = ({
  selectedAddOns,
  setSelectedAddOns,
  setTotalAddOnPrice,
  addOns,
  captureSnapshot,
}) => {
  const { data: addOnsData, loading, error } = addOns;
  const totalPrice = useMemo(() => {
    return selectedAddOns?.reduce((sum, item) => sum + item.price, 0) || 0;
  }, [selectedAddOns]);
  useEffect(() => {
    setTotalAddOnPrice(totalPrice);
  }, [totalPrice, setTotalAddOnPrice]);
  // Memoized item ID generator
  const getItemId = useCallback((addOn, groupId) => {
    return `${groupId}-${addOn._id || addOn.name}`;
  }, []);

  // Memoized image URL generator
  const getImageUrl = useCallback((imagePath) => {
    if (!imagePath)
      return "https://via.pzlaceholder.com/120x80/cccccc/666666?text=No+Image";
    if (imagePath.startsWith("http")) return imagePath;
    return `https://users.bestembroidery.org${imagePath}`;
  }, []);

  // Optimized checkbox handler
  const toggleItemSelection = useCallback(
    (addOn, groupId, groupTitle) => {
      const itemId = getItemId(addOn, groupId);

      setSelectedAddOns((prev) => {
        const exists = prev.some((item) => item.id === itemId);
        if (exists) {
          return prev.filter((item) => item.id !== itemId);
        } else {
          return [
            ...prev,
            {
              id: itemId,
              addOnId: addOn._id,
              groupId,
              groupTitle,
              name: addOn.name,
              price: addOn.price,
              image: addOn.image,
            },
          ];
        }
      });
      // Capture snapshot for undo/redo
      setTimeout(() => captureSnapshot?.(), 100);
    },
    [getItemId, setSelectedAddOns, captureSnapshot],
  );

  // Optimized selection checker
  const isSelected = useCallback(
    (addOn, groupId) => {
      const itemId = getItemId(addOn, groupId);
      return selectedAddOns?.some((item) => item.id === itemId) || false;
    },
    [selectedAddOns, getItemId],
  );

  // Optimized remove item handler
  const removeItem = useCallback(
    (itemId) => {
      setSelectedAddOns((prev) => prev.filter((item) => item.id !== itemId));
      // Capture snapshot for undo/redo
      setTimeout(() => captureSnapshot?.(), 100);
    },
    [setSelectedAddOns, captureSnapshot],
  );

  // Loading state
  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading add-ons...</p>
        </div>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger" className="shadow-sm">
          <Alert.Heading>Error Loading Add-Ons</Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      </Container>
    );
  }

  // No data state
  if (!addOnsData?.data?.docs?.length) {
    return (
      <Container className="py-4">
        <Alert variant="info" className="shadow-sm">
          <Alert.Heading>No Add-Ons Available</Alert.Heading>
          <p className="mb-0">Please check back later for available add-ons.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      fluid
      className="py-4"
      style={{
        backgroundColor: "#E8E9ED",
        borderRadius: "10px",
        minHeight: "400px",
        maxHeight: "870px",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Selected Add-Ons Summary */}
      <div className="pt-3 pb-1 fw-bold fs-5">Add-Ons :</div>
      {selectedAddOns?.length > 0 && (
        <div className="bg-light p-3 rounded">
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold fs-6 mb-0">Total Add-Ons Price</span>
            <div className="d-flex gap-4">
              <span className="fw-bold fs-6">${totalPrice}</span>
            </div>
          </div>
        </div>
      )}
      {/* Add-Ons Selection */}
      <div className="add-ons-section py-4">
        {addOnsData.data.docs.map((group) => (
          <div key={group._id} className="mb-5">
            <div className="mb-4">
              <h2 className="mb-2 fs-6 fw-bold">{group.title}</h2>
              <div className="d-flex align-items-center">
                <Badge bg="secondary" className="me-2">
                  {group.add_ons.length}{" "}
                  {group.add_ons.length === 1 ? "option" : "options"}
                </Badge>
                <small className="text-muted  ">Select one or multiple</small>
              </div>
            </div>

            <Row className="g-3">
              {group.add_ons.map((addOn) => {
                const selected = isSelected(addOn, group._id);

                return (
                  <Col key={addOn._id} xs={6} lg={6} xl={4}>
                    <Card
                      className={`h-100 shadow-sm border-2 ${
                        selected
                          ? "border-primary bg-primary-light"
                          : "border-light"
                      }`}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        toggleItemSelection(addOn, group._id, group.title)
                      }
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        toggleItemSelection(addOn, group._id, group.title)
                      }
                      style={{
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        height: "170px", // 🔥 Reduced height
                        "--bs-primary-light": "rgba(13,110,253,0.05)",
                      }}
                    >
                      <Card.Body className="p-0">
                        <div className="d-flex h-100">
                          {/* ---------- 30% IMAGE BLOCK ---------- */}
                          <div
                            className="flex-shrink-0"
                            style={{ width: "30%" }}
                          >
                            <div className="h-100 w-100 bg-light position-relative overflow-hidden">
                              <img
                                loading="lazy"
                                src={getImageUrl(addOn.image)}
                                alt={addOn.name}
                                className="w-100 h-100"
                                style={{ objectFit: "cover" }}
                                onError={(e) =>
                                  (e.target.src =
                                    "https://via.placeholder.com/120x120/cccccc/666666?text=No+Image")
                                }
                              />
                              {selected && (
                                <div className="position-absolute top-0 start-0 bg-primary text-white px-2 py-1 small">
                                  Selected
                                </div>
                              )}
                            </div>
                          </div>

                          {/* ---------- 70% TEXT BLOCK ---------- */}
                          <div
                            className="flex-grow-1 p-2 d-flex flex-column"
                            style={{ width: "70%" }}
                          >
                            <div className="mb-auto">
                              <Card.Title
                                className="truncate-two-lines mb-1"
                                style={{ height: "5rem", fontSize:"1.5rem"}}
                              >
                               {addOn.name}
                              </Card.Title>

                              {/* <Card.Subtitle className="text-muted small mb-1">
                                {group.title}
                              </Card.Subtitle> */}

                              {addOn.description && (
                                <Card.Text className="small text-muted mb-1 text-truncate">
                                  {addOn.description}
                                </Card.Text>
                              )}
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-1">
                              <h3
                              
                                style={{ fontSize: "1.5rem",color:"#8a8a8a" }}
                              >
                                ${addOn.price}
                              </h3>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}
      </div>
    </Container>
  );
};
